import { RefObject, useEffect, useRef } from "react";

// A mouse notch arrives as a delta of about 100, a trackpad pinch as a stream of very small ones. Feeding
// both through exp() turns a notch into a comfortable step while still letting a pinch resolve smoothly.
const ZOOM_SENSITIVITY = 0.002;

/**
 * Ctrl (or Command) held while scrolling over `elementRef` zooms the preview rather than scrolling it, and
 * reports the change as a factor so it drives the same zoom the toolbar shows. A trackpad pinch reaches the
 * browser as this very event, so it zooms too.
 */
export function useCtrlWheelZoom(elementRef: RefObject<HTMLElement | null>, onZoomBy: ((factor: number) => void) | undefined) {
    // The callback is rebuilt on every parent render. Holding it in a ref keeps the listener, which has to be
    // registered by hand, from being torn down and re-added on each of them.
    const onZoomByRef = useRef(onZoomBy);
    onZoomByRef.current = onZoomBy;

    useEffect(() => {
        const element = elementRef.current;
        if (!element) {
            return;
        }

        // Registered manually rather than through an onWheel prop: React attaches wheel listeners passively at
        // the root, where preventDefault() is ignored and the browser zooms the whole application instead.
        const handleWheel = (event: WheelEvent) => {
            if (!event.ctrlKey && !event.metaKey) {
                return;
            }
            event.preventDefault();
            onZoomByRef.current?.(Math.exp(-event.deltaY * ZOOM_SENSITIVITY));
        };

        element.addEventListener("wheel", handleWheel, { passive: false });
        return () => element.removeEventListener("wheel", handleWheel);
    }, [elementRef]);
}
